/**
 * TopBar Component (Issue #703 Phase 2, Issue #705 Desktop Navigation)
 *
 * Responsive app header with club-first navigation:
 * - Mobile: Back button (optional, left) + Club name / conversation selector
 *   (center) + Avatar (right). Mobile primary nav is BottomNav, not a hamburger
 *   here (ADR-0005 §2.7, #1976).
 * - Desktop: Inline nav links (left) + Club name (center, absolute) + Avatar (right)
 *
 * Related: ADR-0005 Navigation Shell & Menu Architecture, Issue #695 Club-First Navigation
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, User } from 'lucide-react';
import BottomSheet from './BottomSheet';
import ClubList from './ClubList';
import { useClub, useClubs } from '../hooks/useClubs';
import { useAuth } from '../hooks/useAuth';
import { useClubNavContext } from '../hooks/useClubNavContext';
import { MAIN_NAV_ITEMS, ADMIN_NAV_ITEMS, isAdminRoute } from '../config/navigationConfig';

interface TopBarProps {
  title?: string; // Optional - will auto-detect from club context (Issue #703)
  subtitle?: string; // Optional context subtitle (e.g., room name) - displayed uppercase (#857)
  onBack?: () => void | Promise<void>; // Optional back navigation handler
  showClubSwitcher?: boolean; // Show club switcher using nav context (#742)
  /**
   * #1976: when provided (chat context), the center shows the club title as
   * PLAIN text and the room label as its own `{label} ▾` selector that opens
   * the ContextSheet (one trigger → combined club + conversation sheet, Phase 2).
   * Takes precedence over the club-switch button — the misleading club chevron
   * is NOT rendered in this state (Frank decision #1).
   */
  conversationSelector?: { label: string; onOpen: () => void };
}

export default function TopBar({ title, subtitle, onBack, showClubSwitcher, conversationSelector }: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams<{ slug?: string }>();
  const { user } = useAuth();

  // Resolve club context with memory (Issue #724)
  const clubSlugForNav = useClubNavContext();

  // #1358: Only use URL slug when actually on a club route
  // Other routes (like /help/:category/:slug) may have :slug param for different purposes
  const isClubRoute = location.pathname.startsWith('/clubs/');

  // Determine which slug to use for club data fetch
  // - URL slug takes priority (for club pages only)
  // - Nav context used when showClubSwitcher is true (#742)
  const effectiveSlug = isClubRoute
    ? (slug || (showClubSwitcher ? clubSlugForNav : null))
    : (showClubSwitcher ? clubSlugForNav : null);

  // Only fetch club when we have an effective slug
  const { data: clubData } = useClub(effectiveSlug || '');

  // The user's clubs back the club switcher sheet (#1976 Phase 2 — replaced
  // ClubSwitcherModal with the shared BottomSheet + ClubList).
  const { data: myClubs, isLoading: clubsLoading, error: clubsError } = useClubs();

  // Menu states
  const [isClubSwitcherOpen, setIsClubSwitcherOpen] = useState(false);

  // Resolve title: prop override → club name → fallback "ORO move"
  const resolvedTitle = title || (effectiveSlug && clubData?.club?.name) || 'ORO move';

  // Show club switcher if URL has slug OR showClubSwitcher prop is true with valid context
  const shouldShowClubSwitcher = slug || (showClubSwitcher && effectiveSlug);

  // Helper: Resolve nav path based on item kind (Issue #705)
  const getNavPath = (item: (typeof MAIN_NAV_ITEMS)[0]): string => {
    if (item.kind === 'clubScoped' && clubSlugForNav) {
      switch (item.id) {
        case 'home':
          return `/clubs/${clubSlugForNav}`;
        case 'events':
          return `/clubs/${clubSlugForNav}/events`;
        case 'discuss':
          return `/clubs/${clubSlugForNav}/chat`;
        default:
          return item.path;
      }
    }
    return item.path;
  };

  // Helper: Check if nav item is active (matches BottomNav pattern)
  const isNavActive = (item: (typeof MAIN_NAV_ITEMS)[0]): boolean => {
    if (item.id === 'home' && clubSlugForNav) {
      return location.pathname === `/clubs/${clubSlugForNav}` || location.pathname === '/home';
    }
    if (item.id === 'events' && clubSlugForNav) {
      return location.pathname.startsWith(`/clubs/${clubSlugForNav}/events`);
    }
    if (item.id === 'discuss' && clubSlugForNav) {
      return location.pathname.startsWith(`/clubs/${clubSlugForNav}/chat`);
    }
    return location.pathname.startsWith(item.path);
  };

  // Helper: Handle nav click with proper state
  const handleNavClick = (item: (typeof MAIN_NAV_ITEMS)[0]) => {
    const path = getNavPath(item);
    const state =
      item.id === 'home'
        ? { fromHomeTab: true }
        : item.id === 'events'
          ? { fromEventsTab: true }
          : item.id === 'discuss'
            ? { fromDiscussTab: true }
            : undefined;
    navigate(path, { state });
  };

  // Handle escape key to close the club switcher
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsClubSwitcherOpen(false);
      }
    }

    if (isClubSwitcherOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isClubSwitcherOpen]);

  const openClubSwitcher = () => {
    setIsClubSwitcherOpen(true);
  };

  const closeClubSwitcher = () => {
    setIsClubSwitcherOpen(false);
  };

  // Active club for the switcher: explicit nav-context (#742) else the URL slug.
  const activeClubSlug = (showClubSwitcher ? clubSlugForNav : undefined) ?? slug;

  const handleClubSelect = (newSlug: string) => {
    if (showClubSwitcher) {
      // Non-club-scoped pages (Activity, etc.) - update sessionStorage, stay on page (#742)
      try {
        sessionStorage.setItem('oromove:lastClubSlug', newSlug);
      } catch {
        // Graceful degradation
      }
      navigate(location.pathname, { replace: true });
    } else if (slug) {
      // Club-scoped pages (Chat, Events, etc.) - same page, new club (#746)
      navigate(location.pathname.replace(`/clubs/${slug}`, `/clubs/${newSlug}`));
    } else {
      navigate(`/clubs/${newSlug}`);
    }
    closeClubSwitcher();
  };

  const handleAvatarClick = () => {
    navigate('/me');
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-oro-surface border-b border-oro-border" style={{ zIndex: 40 }}>
        <div className="relative flex items-center justify-between h-14 px-4">
          {/* Left section: Back button (optional) / Nav links (desktop).
              Mobile primary nav is BottomNav (ADR-0005) — no hamburger here. */}
          <div className="flex items-center gap-1">
            {/* Back Button (Issue #858) - shown when onBack is provided */}
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center justify-center w-11 h-11 rounded-lg hover:bg-oro-surface-alt transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="w-5 h-5 text-text-primary" />
              </button>
            )}

            {/* Desktop Navigation Links (Issue #705) */}
            {/* Use ADMIN_NAV_ITEMS for platform admin routes (#1088) */}
            <nav className="hidden md:flex items-center gap-1">
            {(isAdminRoute(location.pathname) ? ADMIN_NAV_ITEMS : MAIN_NAV_ITEMS).map((item) => {
              const isActive = isNavActive(item);
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="flex items-center gap-2 px-3 py-2 min-h-11 rounded-lg hover:bg-oro-surface-alt transition-colors"
                  style={{ color: isActive ? 'var(--oro-gold)' : 'var(--text-muted)' }}
                  type="button"
                >
                  {item.icon && <item.icon className="w-5 h-5" />}
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
            </nav>
          </div>

          {/* Center: Club Name (or Title) + Subtitle + Chevron - Absolute centered on desktop */}
          <div className="md:absolute md:left-1/2 md:-translate-x-1/2">
            {conversationSelector ? (
              /* #1976 (chat): a SINGLE context selector. Club name (top, compact)
                 + room label ▾ (bottom) live inside ONE ≥44px button that opens
                 the ContextSheet (combined club + conversation switcher, Phase 2).
                 No club-switch chevron / "Switch club" affordance (Frank #1 — the
                 misleading control is gone). Both compact lines fit WITHIN the
                 44px button, so the center never exceeds the 56px (h-14) header —
                 a plain title stacked ABOVE a 44px button would overflow (Frank P1).
                 Club switching lives INSIDE the sheet (>1-club section), not as a
                 second header selector — see #1976 Phase 2 decision. */
              <button
                onClick={conversationSelector.onOpen}
                className="flex items-center gap-1 px-3 min-h-11 max-h-14 rounded-lg hover:bg-oro-surface-alt transition-colors"
                aria-label={`Switch conversation, currently ${conversationSelector.label}`}
                aria-haspopup="dialog"
                data-tour="conversation-selector"
              >
                <span className="flex flex-col items-center min-w-0 leading-tight">
                  <span className="text-sm font-semibold text-text-primary truncate max-w-[180px]">
                    {resolvedTitle}
                  </span>
                  <span className="text-[11px] text-text-muted uppercase tracking-wider truncate max-w-[180px]">
                    {conversationSelector.label}
                  </span>
                </span>
                <ChevronDown className="w-4 h-4 text-text-secondary flex-shrink-0" />
              </button>
            ) : shouldShowClubSwitcher ? (
              <button
                onClick={openClubSwitcher}
                className="flex items-center gap-1.5 px-3 py-1.5 min-h-11 rounded-lg hover:bg-oro-surface-alt transition-colors"
                aria-label="Switch club"
                data-tour="club-switcher"
              >
                <div className="flex flex-col items-center min-w-0">
                  <span className="text-base font-semibold text-text-primary truncate max-w-[200px]">
                    {resolvedTitle}
                  </span>
                  {subtitle && (
                    <span className="text-[11px] text-text-muted uppercase tracking-wider truncate max-w-[200px]">
                      {subtitle}
                    </span>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-text-secondary flex-shrink-0" />
              </button>
            ) : (
              <div className="flex flex-col items-center">
                <h1 className="text-base font-semibold text-text-primary truncate max-w-[200px]">
                  {resolvedTitle}
                </h1>
                {subtitle && (
                  <span className="text-[11px] text-text-muted uppercase tracking-wider truncate max-w-[200px]">
                    {subtitle}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right: User Avatar */}
          <button
            onClick={handleAvatarClick}
            className="flex items-center justify-center w-11 h-11 rounded-lg hover:bg-oro-surface-alt transition-colors"
            aria-label="Profile"
          >
            {user?.profile_image_url ? (
              <img
                src={user.profile_image_url}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <User className="w-5 h-5 text-text-primary" />
            )}
          </button>
        </div>

      </header>

      {/* Club switcher — shared BottomSheet + ClubList (#1976 Phase 2, retired ClubSwitcherModal) */}
      <BottomSheet isOpen={isClubSwitcherOpen} onClose={closeClubSwitcher} title="Switch Club">
        <ClubList
          clubs={myClubs ?? []}
          activeClubSlug={activeClubSlug}
          onSelectClub={handleClubSelect}
          isLoading={clubsLoading}
          error={clubsError}
        />
      </BottomSheet>
    </>
  );
}
